import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '../api/axios'
import { CheckCircle, Mail, AlertTriangle } from 'lucide-react'

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const subscriptionId = searchParams.get('token') || searchParams.get('subscription_id')
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [isNewClient, setIsNewClient] = useState(false)
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (!subscriptionId) { setStatus('error'); return }
    const token = searchParams.get('token') || subscriptionId
    api.post('/payment/capture-order', { orderId: token })
      .then(res => {
        setIsNewClient(res.data.isNewClient)
        setEmail(res.data.email || '')
        setStatus('success')
      })
      .catch(() => setStatus('error'))
  }, [subscriptionId])

  return (
    <div style={{ minHeight: '100vh', background: '#0B0F19', fontFamily: 'Poppins, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#1A1F2E', borderRadius: 20, padding: '48px 40px', maxWidth: 480, width: '100%', textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <div style={{ width: 60, height: 60, border: '4px solid #FF7F32', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 24px', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#9CA3AF', fontSize: 16 }}>Activating your subscription...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ width: 72, height: 72, background: 'rgba(34,197,94,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36 }}>
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 700, marginBottom: 12 }}>
              {isNewClient ? 'Welcome to Inkcredible!' : 'Subscription Renewed!'}
            </h1>
            {isNewClient ? (
              <>
                <p style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                  Your account has been created and your subscription is now active.
                  Login credentials have been sent to <strong style={{ color: '#fff' }}>{email}</strong>.
                </p>
                <p style={{ color: '#FF7F32', fontSize: 13, marginBottom: 16 }}>
                  ⚠ You will be asked to change your password on first login.
                </p>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px', marginBottom: 24, textAlign: 'left' as const }}>
                  <p style={{ color: '#111827', fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={13} /> Submitting Jobs</p>
                  <p style={{ color: '#6B7280', fontSize: 12, lineHeight: 1.6 }}>
                    You can submit jobs directly through the portal after logging in.
                    If you prefer to submit via email, our team will set up a dedicated inbox for you within 24 hours.
                  </p>
                </div>
              </>
            ) : (
              <p style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Your subscription has been renewed successfully. You can now continue submitting jobs.
              </p>
            )}
            <button
              onClick={() => navigate('/login')}
              style={{ background: '#FF7F32', color: '#fff', fontWeight: 700, fontSize: 15, padding: '14px 32px', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
              Go to Portal Login
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ marginBottom: 24 }}><AlertTriangle size={48} className="text-yellow-500" /></div>
            <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Something went wrong</h1>
            <p style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              Your payment may have been processed but we couldn't activate your subscription automatically.
              Please contact us with your payment details and we'll sort it out immediately.
            </p>
            <a href="https://subscribe.inkcrediblestudios.com#contact"
              style={{ background: '#FF7F32', color: '#fff', fontWeight: 700, fontSize: 14, padding: '12px 28px', borderRadius: 10, textDecoration: 'none', display: 'inline-block' }}>
              Contact Support
            </a>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
